import { Space, Typography } from "antd";
import { groupBy } from "lodash-es";
import days from "dayjs";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import PermissionCheck from "../../Common/PermissionCheck";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import ModalEditSeries from "./ModalEditSeries";
import RemoveSeries from "./RemoveSeries";
import { findImageByTypes } from "../../utilities/findImageByTypes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import { ImageSizes } from "~enums/imageSizes.ts";
import ImageView from "~components/ImageView/ImageView.tsx";

const PermissionName = PermissionNames.SEASON_SERIES;

const calcResultMatch = ({ competitors, seriesType }) => {
  let TEAM_1_RESULT = "secondary";
  let TEAM_2_RESULT = "secondary";

  if (seriesType === "BEST_OF") {
    if (competitors?.[0]?.seriesWins > competitors?.[1]?.seriesWins) {
      TEAM_1_RESULT = "success";
      TEAM_2_RESULT = "danger";
    }

    if (competitors?.[0]?.seriesWins < competitors?.[1]?.seriesWins) {
      TEAM_1_RESULT = "danger";
      TEAM_2_RESULT = "success";
    }

    return {
      TEAM_1_RESULT,
      TEAM_2_RESULT,
    };
  }

  if (competitors?.[0]?.combinedScore > competitors?.[1]?.combinedScore) {
    TEAM_1_RESULT = "success";
    TEAM_2_RESULT = "danger";
  }

  if (competitors?.[0]?.combinedScore < competitors?.[1]?.combinedScore) {
    TEAM_1_RESULT = "danger";
    TEAM_2_RESULT = "success";
  }

  return {
    TEAM_1_RESULT,
    TEAM_2_RESULT,
  };
};

const RenderScoreResult = ({ competitors, seriesType, entities, organizationId }) => {
  if (!competitors?.length) return "";

  const { TEAM_1_RESULT, TEAM_2_RESULT } = calcResultMatch({
    competitors,
    seriesType,
  });
  let SCHEDULE_GAME = false;
  // checking for schedule game

  if (competitors?.[0]?.seriesLosses + competitors?.[0]?.seriesDraws + competitors?.[0]?.seriesWins === 0) {
    SCHEDULE_GAME = true;
  }

  const TEAM_1 = entities?.[`${organizationId}:${competitors?.[0]?.entityId}`];
  const TEAM_2 = entities?.[`${organizationId}:${competitors?.[1]?.entityId}`];

  const TEAM_1_LOGO = findImageByTypes(TEAM_1?.images, [ImageTypes.Logo], ImageSizes.Small)
  const TEAM_2_LOGO = findImageByTypes(TEAM_2?.images, [ImageTypes.Logo], ImageSizes.Small)

  const TEAM_1_SCORE = SCHEDULE_GAME
    ? "-"
    : seriesType === "BEST_OF"
      ? competitors?.[0]?.seriesWins
      : competitors?.[0]?.combinedScore;
  const TEAM_2_SCORE = SCHEDULE_GAME
    ? "-"
    : seriesType === "BEST_OF"
      ? competitors?.[1]?.seriesWins
      : competitors?.[1]?.combinedScore;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Space style={{ flex: 1, justifyContent: "end" }}>
        <ImageView imageType={ImageTypes.Logo} size={ImageSizes.Small} height={25} width={25} imageUrl={TEAM_1_LOGO.url} />
        <Typography.Text>{TEAM_1?.nameFullLocal}</Typography.Text>
        <Typography.Text type={SCHEDULE_GAME ? "secondary" : TEAM_1_RESULT} strong>
          ({TEAM_1_SCORE})
        </Typography.Text>{" "}
      </Space>
      <Space style={{ flex: 1, justifyContent: "start" }}>
        <Typography.Text type={SCHEDULE_GAME ? "secondary" : TEAM_2_RESULT} strong>
          ({TEAM_2_SCORE})
        </Typography.Text>
        {TEAM_2?.nameFullLocal}
        <ImageView imageType={ImageTypes.Logo} size={ImageSizes.Small} height={25} width={25} imageUrl={TEAM_2_LOGO.url} />
      </Space>
    </div>
  );
};

const useGetSeriesListColumns = ({ seriesData, stages, fetchSeriesList, includedData }) => {
  const { formatMessage } = useIntl();
  const { organizationId } = useParams();

  return [
    {
      dataIndex: "seriesCode",
    },
    {
      title: formatMessage({
        id: "series.name",
        defaultMessage: "Series Name",
      }),
      dataIndex: "nameLocal",
    },
    {
      title: formatMessage({
        id: "series.type",
        defaultMessage: "Series Type",
      }),
      dataIndex: "seriesType",
      filterSearch: true,
      filters:
        Object.keys(groupBy(seriesData, "seriesType"))?.map((seriesType) => {
          return {
            text: formatMessage({
              id: `series.type.${seriesType}`,
              defaultMessage: seriesType,
            }),
            value: seriesType,
          };
        }) || [],
      onFilter: (value, record) => record.seriesType === value,
      render: (seriesType) =>
        formatMessage({
          id: `series.type.${seriesType}`,
          defaultMessage: seriesType,
        }),
    },
    {
      title: "Teams",
      dataIndex: "competitors",
      align: "center",
      render: (competitors, record) => (
        <RenderScoreResult
          organizationId={organizationId}
          entities={includedData?.entities}
          competitors={competitors}
          seriesType={record?.seriesType}
        />
      ),
    },
    {
      title: formatMessage({
        id: "max.fixtures.number",
        defaultMessage: "Number of Fixtures",
      }),
      dataIndex: "maxFixturesNumber",
    },
    {
      title: formatMessage({ id: "stage", defaultMessage: "Stage" }),
      dataIndex: "stageName",
      filters: stages?.map((stage) => {
        return {
          text: stage?.nameLocal,
          value: stage?.stageCode,
        };
      }),
      onFilter: (value, record) => record.stageCode === value,
    },
    {
      title: formatMessage({ id: "status", defaultMessage: "Status" }),
      dataIndex: "status",
      filterSearch: true,
      filters:
        Object.keys(groupBy(seriesData, "status"))?.map((status) => {
          return {
            text: formatMessage({
              id: `series.status.${status}`,
              defaultMessage: status,
            }),
            value: status,
          };
        }) || [],
      onFilter: (value, record) => record.status === value,
      render: (status) =>
        formatMessage({
          id: `series.status.${status}`,
          defaultMessage: status,
        }),
    },
    {
      title: formatMessage({
        id: "date.start",
        defaultMessage: "Start Date",
      }),
      dataIndex: "startDate",
      render: (startDate) => (startDate ? days(startDate).format("DD.MM.YYYY") : ""),
    },
    {
      title: formatMessage({
        id: "date.end",
        defaultMessage: "End Date",
      }),
      dataIndex: "endDate",
      render: (endDate) => (endDate ? days(endDate).format("DD.MM.YYYY") : ""),
    },
    {
      width: 1,
      render: (item) => (
        <Space>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <ModalEditSeries series={item} fetchSeriesList={fetchSeriesList} />
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
            <RemoveSeries series={item} fetchSeriesList={fetchSeriesList} />
          </PermissionCheck>
        </Space>
      ),
    },
  ];
};

export default useGetSeriesListColumns;
